<script setup lang="ts">
import { useId } from 'vue'
import { storeToRefs } from 'pinia'

import { useMockupEditorStore } from '@/stores/mockupEditor'

const store = useMockupEditorStore()
const { editorLayerToast } = storeToRefs(store)
const uid = useId()
const titleId = `${uid}-title`
const bodyId = `${uid}-body`
</script>

<template>
  <Teleport to="body">
    <Transition name="im-layer-toast">
      <div
        v-if="editorLayerToast"
        class="backdrop"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        :aria-describedby="bodyId"
        @click.self="store.dismissEditorLayerToast"
      >
        <div
          class="card"
          :class="editorLayerToast.variant === 'error' ? 'card--error' : 'card--info'"
          @click.stop
        >
          <button
            type="button"
            class="close"
            :aria-label="store.tUi('inspectorExport.layerToastDismissAria')"
            @click="store.dismissEditorLayerToast"
          >
            ×
          </button>
          <h3 :id="titleId" class="title">{{ editorLayerToast.title }}</h3>
          <p :id="bodyId" class="body">{{ editorLayerToast.body }}</p>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.backdrop {
  position: fixed;
  inset: 0;
  z-index: 8000;
  display: grid;
  place-items: center;
  padding: 1.25rem;
  background: rgba(15, 23, 42, 0.38);
  backdrop-filter: blur(6px);
}

.card {
  position: relative;
  width: min(440px, calc(100vw - 2.5rem));
  border-radius: var(--im-radius-lg);
  border: 1px solid var(--im-border);
  background: var(--im-surface);
  color: var(--im-text);
  box-shadow: var(--im-shadow), 0 24px 48px rgba(15, 23, 42, 0.12);
  padding: 1.15rem 1.25rem 1.15rem 1.35rem;
  text-align: left;
}

.card--error {
  border-left: 4px solid var(--im-danger);
}

.card--info {
  border-left: 4px solid var(--im-interactive-bg);
}

.close {
  position: absolute;
  top: 0.55rem;
  right: 0.55rem;
  width: 2rem;
  height: 2rem;
  border: 0;
  border-radius: var(--im-radius-sm);
  background: transparent;
  color: var(--im-muted-2);
  font-size: 1.35rem;
  line-height: 1;
  cursor: pointer;
  display: grid;
  place-items: center;
}

.close:hover {
  background: var(--im-interactive-soft);
  color: var(--im-text);
}

.close:focus-visible {
  outline: 2px solid var(--im-interactive-bg);
  outline-offset: 2px;
}

.title {
  margin: 0 2rem 0.45rem 0;
  font-size: 0.98rem;
  font-weight: 900;
  letter-spacing: -0.02em;
  color: var(--im-text);
}

.body {
  margin: 0;
  font-size: 0.84rem;
  line-height: 1.55;
  color: var(--im-muted);
  white-space: pre-line;
}

.im-layer-toast-enter-active,
.im-layer-toast-leave-active {
  transition: opacity 200ms ease;
}

.im-layer-toast-enter-active .card,
.im-layer-toast-leave-active .card {
  transition:
    transform 220ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 200ms ease;
}

.im-layer-toast-enter-from,
.im-layer-toast-leave-to {
  opacity: 0;
}

.im-layer-toast-enter-from .card,
.im-layer-toast-leave-to .card {
  opacity: 0;
  transform: translateY(10px) scale(0.98);
}
</style>
